import { PlanWeightsEnum, RatchetModelsEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { PaperPlanPlanType, PlanWeightsTab } from 'src/constants';
import { IPlanValues } from 'src/types/IPaperPlan';
import { IWeightsProfile } from 'src/types/IWeightsProfile';

export const NUMERATOR_WEIGHT_NAME = 'Numerator';
export const DENOMINATOR_WEIGHT_NAME = 'Denominator';
export const SINGULAR_WEIGHT_NAME = 'Weight';
export const AMU_ALEXA_PSM_NAME = 'AMU Alexa PSM';
export const PRIME_PRIMARY_CONVERSION_NAME = 'Prime primary conversion';
export const PRIME_SECONDARY_CONVERSION_NAME = 'Prime secondary conversion';
export const AMF_CONVERSION_NAME = 'AMF conversion';
export const AMU_BLENDED_PSM_NAME = 'AMU blended PSM';
export const AMU_DISCOUNT_1_PRE_SPATIAL_NAME = 'AMU discount 1 & 2 Pre-Spatial';
export const AMU_DISCOUNT_2_PRE_SPATIAL_NAME = 'AMU discount 1 & 2 Pre-Spatial (ASD Tier 2)';
export const AMU_DISCOUNT_1_POST_SPATIAL_NAME = 'AMU discount 1 & 2 Post-Spatial';
export const AMU_DISCOUNT_2_POST_SPATIAL_NAME = 'AMU discount 1 & 2 Post-Spatial (ASD Tier 2)';
export const AMU_DISCOUNT_3_NAME = 'AMU discount 3';
export const AMU_DISCOUNT_4_FT_NAME = 'AMU discount 4 FT';
export const AMU_DISCOUNT_4_AMU_NAME = 'AMU discount 4 AMU';

export const DEFAULT_PLAN_WEIGHTS_MAP: Map<PlanWeightsTab, IWeightsProfile> = new Map([
    [
        PlanWeightsTab.WMG,
        {
            primePrimaryNumerator: '',
            primePrimaryDenominator: '',
            primeSecondaryNumerator: '',
            primeSecondaryDenominator: '',
            amfConversionNumerator: '',
            amfConversionDenominator: '',
            amuBlendedPsm: ''
        }
    ],
    [
        PlanWeightsTab.UMG,
        {
            primePrimaryNumerator: '',
            primePrimaryDenominator: '',
            amfConversionNumerator: '',
            amfConversionDenominator: '',
            amuAlexaPsmNumerator: '',
            amuAlexaPsmDenominator: '',
            amuDiscount1PreSpatial: '',
            amuDiscount2PreSpatial: '',
            amuDiscount1PostSpatial: '',
            amuDiscount2PostSpatial: '',
            amuDiscount3: '',
            amuDiscount4Ft: '',
            amuDiscount4Amu: ''
        }
    ],
    [
        PlanWeightsTab.SONY,
        {
            primePrimaryNumerator: '',
            primePrimaryDenominator: '',
            amfConversionNumerator: '',
            amfConversionDenominator: '',
            amuAlexaPsmNumerator: '',
            amuAlexaPsmDenominator: ''
        }
    ],
    [
        PlanWeightsTab.ORCHARD,
        {
            primePrimaryNumerator: '',
            primePrimaryDenominator: '',
            amfConversionNumerator: '',
            amfConversionDenominator: ''
        }
    ],
    [
        PlanWeightsTab.SONY_JP,
        {
            primePrimaryNumerator: '',
            primePrimaryDenominator: '',
            amfConversionNumerator: '',
            amfConversionDenominator: '',
            amuAlexaPsmNumerator: '',
            amuAlexaPsmDenominator: ''
        }
    ]
]);

const NUMERATOR_AND_DENOMINATOR_WEIGHTS = [NUMERATOR_WEIGHT_NAME, DENOMINATOR_WEIGHT_NAME];
const SINGLE_WEIGHT = [SINGULAR_WEIGHT_NAME];
const AMU_ALEXA_PSM = {
    name: AMU_ALEXA_PSM_NAME,
    inputValues: NUMERATOR_AND_DENOMINATOR_WEIGHTS
};

type AddWeightsInputs = {
    name: string;
    inputValues: string[];
}[];

const ADD_WEIGHTS_BASE_INPUTS: AddWeightsInputs = [
    {
        name: AMF_CONVERSION_NAME,
        inputValues: NUMERATOR_AND_DENOMINATOR_WEIGHTS
    }
];
const ADD_WEIGHTS_ORCHARD_INPUTS: AddWeightsInputs = [
    {
        name: PRIME_PRIMARY_CONVERSION_NAME,
        inputValues: NUMERATOR_AND_DENOMINATOR_WEIGHTS
    },
    {
        name: AMF_CONVERSION_NAME,
        inputValues: NUMERATOR_AND_DENOMINATOR_WEIGHTS
    }
];
const ADD_WEIGHTS_WMG_BASE_INPUTS: AddWeightsInputs = [
    {
        name: PRIME_PRIMARY_CONVERSION_NAME,
        inputValues: NUMERATOR_AND_DENOMINATOR_WEIGHTS
    },
    {
        name: PRIME_SECONDARY_CONVERSION_NAME,
        inputValues: NUMERATOR_AND_DENOMINATOR_WEIGHTS
    },
    {
        name: AMF_CONVERSION_NAME,
        inputValues: NUMERATOR_AND_DENOMINATOR_WEIGHTS
    }
];
const ADD_WEIGHTS_WMG_INPUTS = [
    ...ADD_WEIGHTS_WMG_BASE_INPUTS,
    {
        name: AMU_BLENDED_PSM_NAME,
        inputValues: SINGLE_WEIGHT
    }
];
const ADD_WEIGHTS_UMG_INPUTS: AddWeightsInputs = [
    ...ADD_WEIGHTS_BASE_INPUTS,
    AMU_ALEXA_PSM,
    {
        name: AMU_DISCOUNT_1_PRE_SPATIAL_NAME,
        inputValues: SINGLE_WEIGHT
    },
    {
        name: AMU_DISCOUNT_2_PRE_SPATIAL_NAME,
        inputValues: SINGLE_WEIGHT
    },
    {
        name: AMU_DISCOUNT_1_POST_SPATIAL_NAME,
        inputValues: SINGLE_WEIGHT
    },
    {
        name: AMU_DISCOUNT_2_POST_SPATIAL_NAME,
        inputValues: SINGLE_WEIGHT
    },
    {
        name: AMU_DISCOUNT_3_NAME,
        inputValues: SINGLE_WEIGHT
    },
    {
        name: AMU_DISCOUNT_4_FT_NAME,
        inputValues: SINGLE_WEIGHT
    },
    {
        name: AMU_DISCOUNT_4_AMU_NAME,
        inputValues: SINGLE_WEIGHT
    }
];
const ADD_WEIGHTS_SONY_INPUTS: AddWeightsInputs = [...ADD_WEIGHTS_BASE_INPUTS, AMU_ALEXA_PSM];
const ADD_WEIGHTS_SONYJP_INPUTS: AddWeightsInputs = [AMU_ALEXA_PSM];

/**
 * Retrieves displayed inputs based on vendor
 */
export const getWeightsInputs = (
    vendor: string
): {
    name: string;
    inputValues: string[];
}[] => {
    switch (vendor) {
        case PlanWeightsTab.WMG:
            return ADD_WEIGHTS_WMG_INPUTS;

        case PlanWeightsTab.UMG:
            return ADD_WEIGHTS_UMG_INPUTS;

        case PlanWeightsTab.SONY:
            return ADD_WEIGHTS_SONY_INPUTS;
        case PlanWeightsTab.SONY_JP:
            return ADD_WEIGHTS_SONYJP_INPUTS;
        case PlanWeightsTab.ORCHARD:
            return ADD_WEIGHTS_ORCHARD_INPUTS;
        default:
            return ADD_WEIGHTS_BASE_INPUTS;
    }
};

/**
 * Retrieves value from either weights/error map based on vendor, name, input value
 */
export const getWeightsFieldFromMap = (
    map: Map<PlanWeightsTab, IWeightsProfile>,
    vendor: PlanWeightsTab,
    name: string,
    inputValue: string
): string | undefined => {
    switch (name) {
        case PRIME_PRIMARY_CONVERSION_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return map.get(vendor)?.primePrimaryNumerator;
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return map.get(vendor)?.primePrimaryDenominator;
            }
            break;

        case PRIME_SECONDARY_CONVERSION_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return map.get(vendor)?.primeSecondaryNumerator;
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return map.get(vendor)?.primeSecondaryDenominator;
            }
            break;

        case AMF_CONVERSION_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return map.get(vendor)?.amfConversionNumerator;
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return map.get(vendor)?.amfConversionDenominator;
            }
            break;

        case AMU_ALEXA_PSM_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return map.get(vendor)?.amuAlexaPsmNumerator;
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return map.get(vendor)?.amuAlexaPsmDenominator;
            }
            break;

        case AMU_BLENDED_PSM_NAME:
            return map.get(vendor)?.amuBlendedPsm;

        case AMU_DISCOUNT_1_PRE_SPATIAL_NAME:
            return map.get(vendor)?.amuDiscount1PreSpatial;

        case AMU_DISCOUNT_2_PRE_SPATIAL_NAME:
            return map.get(vendor)?.amuDiscount2PreSpatial;

        case AMU_DISCOUNT_1_POST_SPATIAL_NAME:
            return map.get(vendor)?.amuDiscount1PostSpatial;

        case AMU_DISCOUNT_2_POST_SPATIAL_NAME:
            return map.get(vendor)?.amuDiscount2PostSpatial;

        case AMU_DISCOUNT_3_NAME:
            return map.get(vendor)?.amuDiscount3;

        case AMU_DISCOUNT_4_FT_NAME:
            return map.get(vendor)?.amuDiscount4Ft;

        case AMU_DISCOUNT_4_AMU_NAME:
            return map.get(vendor)?.amuDiscount4Amu;

        default:
            return undefined;
    }
};

/**
 * Retrieves corresponding key name
 */
export const getWeightsKey = (name: string, inputValue: string): keyof IWeightsProfile | '' => {
    switch (name) {
        case PRIME_PRIMARY_CONVERSION_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return 'primePrimaryNumerator';
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return 'primePrimaryDenominator';
            }
            break;

        case PRIME_SECONDARY_CONVERSION_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return 'primeSecondaryNumerator';
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return 'primeSecondaryDenominator';
            }
            break;

        case AMF_CONVERSION_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return 'amfConversionNumerator';
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return 'amfConversionDenominator';
            }
            break;

        case AMU_ALEXA_PSM_NAME:
            if (inputValue === NUMERATOR_WEIGHT_NAME) {
                return 'amuAlexaPsmNumerator';
            }
            if (inputValue === DENOMINATOR_WEIGHT_NAME) {
                return 'amuAlexaPsmDenominator';
            }
            break;

        case AMU_BLENDED_PSM_NAME:
            return 'amuBlendedPsm';

        case AMU_DISCOUNT_1_PRE_SPATIAL_NAME:
            return 'amuDiscount1PreSpatial';

        case AMU_DISCOUNT_2_PRE_SPATIAL_NAME:
            return 'amuDiscount2PreSpatial';

        case AMU_DISCOUNT_1_POST_SPATIAL_NAME:
            return 'amuDiscount1PostSpatial';

        case AMU_DISCOUNT_2_POST_SPATIAL_NAME:
            return 'amuDiscount2PostSpatial';

        case AMU_DISCOUNT_3_NAME:
            return 'amuDiscount3';

        case AMU_DISCOUNT_4_FT_NAME:
            return 'amuDiscount4Ft';

        case AMU_DISCOUNT_4_AMU_NAME:
            return 'amuDiscount4Amu';
    }

    return '';
};

/**
 * Creates a map with prefilled weights based on business rules
 */
export const getPrefilledWeightsMap = (planValues: IPlanValues): Map<PlanWeightsTab, IWeightsProfile> => {
    // Can't use isPromotion attribute because it is not set until API call
    const isPromotion = !(planValues.customerPays && planValues.royaltyBearing && planValues.renewalCadence);
    const promoDuration = parseFloat(planValues.promoDurationInDays ?? '');

    // WMG
    const wmgBlendedPsm = Number(!isPromotion);
    const wmgPrimePrimaryNumerator = Number(!isPromotion);
    const wmgPrimeSecondaryNumerator = Number(!isPromotion);
    const wmgAmfConversionNumerator = Number(!isPromotion);

    // UMG
    const umgAmuAlexaPsmNumerator = Number(
        !isPromotion &&
            ![PaperPlanPlanType.Student, PaperPlanPlanType.SingleDevice].includes(
                planValues.planType as PaperPlanPlanType
            )
    );
    const umgAmuAlexaPsmDenominator = Number(!isPromotion && planValues.planType === PaperPlanPlanType.SingleDevice);

    let umgPerformanceDiscount1PreSpatial = 0;
    let umgPerformanceDiscount2PreSpatial = 0;
    let umgPerformanceDiscount1PostSpatial = 0;
    let umgPerformanceDiscount2PostSpatial = 0;
    if (!isPromotion) {
        switch (planValues.planType) {
            case PaperPlanPlanType.Student:
                umgPerformanceDiscount1PreSpatial = 0.5;
                umgPerformanceDiscount1PostSpatial = 0.6;
                break;
            case PaperPlanPlanType.SingleDevice:
                umgPerformanceDiscount1PreSpatial = 0.45;
                umgPerformanceDiscount2PreSpatial = 0.5;
                umgPerformanceDiscount1PostSpatial = 0.45;
                umgPerformanceDiscount2PostSpatial = 0.5;
                break;
            case PaperPlanPlanType.Family:
                umgPerformanceDiscount1PreSpatial = 1.5;
                umgPerformanceDiscount1PostSpatial = 1.7;
                break;
            case PaperPlanPlanType.Individual:
                umgPerformanceDiscount1PreSpatial = 1;
                umgPerformanceDiscount1PostSpatial = 1.1;
        }
    }

    const umgPerformanceDiscount3 = Number(!isPromotion);
    const umgPerformanceDiscount4Ft = Number(isPromotion && !planValues.customerPays);
    const umgPerformanceDiscount4Amu = Number(!isPromotion);

    const umgPrimePrimaryNumerator = Number(!isPromotion);
    const umgAmfConversionNumerator = Number(!isPromotion);

    // SONY
    const sonyAmuAlexaPsmNumerator = Number(!isPromotion && planValues.planType === PaperPlanPlanType.SingleDevice);
    let sonyAmuAlexaPsmDenominator = 0;
    if (!isPromotion) {
        sonyAmuAlexaPsmDenominator = 1;
    } else if (planValues.employeePlan) {
        sonyAmuAlexaPsmDenominator = 0;
    } else if (planValues.customerPays) {
        sonyAmuAlexaPsmDenominator = Number(!isNaN(promoDuration) && promoDuration > 90);
    } else {
        sonyAmuAlexaPsmDenominator = Number(!isNaN(promoDuration) && promoDuration > 90);
    }

    let sonyOrchardPrimeAmfNumerator = 0;
    if (!isPromotion && planValues.isHD && planValues.planType === PaperPlanPlanType.Family) {
        sonyOrchardPrimeAmfNumerator = 2;
    } else if (!isPromotion && !planValues.isHD && planValues.planType === PaperPlanPlanType.Family) {
        sonyOrchardPrimeAmfNumerator = 1.5;
    } else if (!isPromotion && planValues.isHD && planValues.planType === PaperPlanPlanType.Individual) {
        sonyOrchardPrimeAmfNumerator = 1.5;
    } else if (!isPromotion && !planValues.isHD && planValues.planType === PaperPlanPlanType.Individual) {
        sonyOrchardPrimeAmfNumerator = 1;
    }

    const prefilledWeightsMap: Map<PlanWeightsTab, IWeightsProfile> = new Map([
        [
            PlanWeightsTab.WMG,
            {
                amuBlendedPsm: wmgBlendedPsm.toString(),
                primePrimaryNumerator: wmgPrimePrimaryNumerator.toString(),
                amfConversionNumerator: wmgAmfConversionNumerator.toString(),
                primePrimaryDenominator: '0',
                primeSecondaryNumerator: wmgPrimeSecondaryNumerator.toString(),
                primeSecondaryDenominator: '0',
                amfConversionDenominator: '0'
            }
        ],
        [
            PlanWeightsTab.UMG,
            {
                amuAlexaPsmNumerator: umgAmuAlexaPsmNumerator.toString(),
                amuAlexaPsmDenominator: umgAmuAlexaPsmDenominator.toString(),
                amuDiscount1PreSpatial: umgPerformanceDiscount1PreSpatial.toString(),
                amuDiscount2PreSpatial: umgPerformanceDiscount2PreSpatial.toString(),
                amuDiscount1PostSpatial: umgPerformanceDiscount1PostSpatial.toString(),
                amuDiscount2PostSpatial: umgPerformanceDiscount2PostSpatial.toString(),
                amuDiscount3: umgPerformanceDiscount3.toString(),
                amuDiscount4Ft: umgPerformanceDiscount4Ft.toString(),
                amuDiscount4Amu: umgPerformanceDiscount4Amu.toString(),
                amfConversionNumerator: umgAmfConversionNumerator.toString(),
                amfConversionDenominator: '0'
            }
        ],
        [
            PlanWeightsTab.SONY,
            {
                amuAlexaPsmNumerator: sonyAmuAlexaPsmNumerator.toString(),
                amuAlexaPsmDenominator: sonyAmuAlexaPsmDenominator.toString(),
                amfConversionNumerator: sonyOrchardPrimeAmfNumerator.toString(),
                amfConversionDenominator: '0'
            }
        ],
        [
            PlanWeightsTab.SONY_JP,
            {
                amuAlexaPsmNumerator: sonyAmuAlexaPsmNumerator.toString(),
                amuAlexaPsmDenominator: sonyAmuAlexaPsmDenominator.toString()
            }
        ],
        [
            PlanWeightsTab.ORCHARD,
            {
                primePrimaryNumerator: sonyOrchardPrimeAmfNumerator.toString(),
                primePrimaryDenominator: '0',
                amfConversionNumerator: sonyOrchardPrimeAmfNumerator.toString(),
                amfConversionDenominator: '0'
            }
        ]
    ]);

    return prefilledWeightsMap;
};

/**
 * Determines which PAPER weight name to use depending on entity and weight
 */
export const getPlanWeightName = (weightType: string, entity: PlanWeightsTab): string => {
    switch (weightType) {
        case 'amuAlexaPsmNumerator':
        case 'amuAlexaPsmDenominator':
            switch (entity) {
                case PlanWeightsTab.WMG:
                    return RatchetModelsEnum.AMU_ALEXA_PSM_WMG_ASD_SUBSCRIPTION;
                case PlanWeightsTab.UMG:
                    return RatchetModelsEnum.AMU_ALEXA_PSM_UMG_ASD_UPGRADES;
                case PlanWeightsTab.SONY:
                case PlanWeightsTab.ORCHARD:
                    return RatchetModelsEnum.AMU_ALEXA_PSM_SONY_ASD_SUBSCRIPTION;
                case PlanWeightsTab.SONY_JP:
                    return RatchetModelsEnum.AMU_ALEXA_PSM_SONY_JP_ASD_SUBSCRIPTION;
            }
            break;

        case 'amfConversionNumerator':
        case 'amfConversionDenominator':
            switch (entity) {
                case PlanWeightsTab.WMG:
                    return RatchetModelsEnum.AMF_CONVERSION_WMG;
                case PlanWeightsTab.UMG:
                    return RatchetModelsEnum.AMF_CONVERSION_UMG;
                case PlanWeightsTab.SONY:
                    return RatchetModelsEnum.AMF_CONVERSION_SONY;
                case PlanWeightsTab.ORCHARD:
                    return RatchetModelsEnum.AMF_CONVERSION_ORCHARD;
                case PlanWeightsTab.SONY_JP:
                    return RatchetModelsEnum.AMF_CONVERSION_SONY;
            }
            break;

        case 'primePrimaryNumerator':
        case 'primePrimaryDenominator':
            switch (entity) {
                case PlanWeightsTab.WMG:
                    return RatchetModelsEnum.PRIME_PRIMARY_CONVERSION_WMG;
                case PlanWeightsTab.UMG:
                    return RatchetModelsEnum.PRIME_CONVERSION_UMG;
                case PlanWeightsTab.SONY:
                    return RatchetModelsEnum.PRIME_CONVERSION_SONY;
                case PlanWeightsTab.ORCHARD:
                    return RatchetModelsEnum.PRIME_CONVERSION_ORCHARD;
                case PlanWeightsTab.SONY_JP:
                    return RatchetModelsEnum.PRIME_CONVERSION_SONY;
            }
            break;

        case 'primeSecondaryNumerator':
        case 'primeSecondaryDenominator':
            // Should only be WMG
            if (entity === PlanWeightsTab.WMG) {
                return RatchetModelsEnum.PRIME_SECONDARY_CONVERSION_WMG;
            }
    }

    return PlanWeightsEnum.RATCHETS;
};
